import { Button, Flex, Input, Progress, Tag, Upload, theme, Typography, notification } from 'antd';
import { ReloadOutlined, CloseOutlined, EditOutlined } from '@ant-design/icons';
import { useState, useRef, useMemo, useEffect, forwardRef, useImperativeHandle } from 'react';
import AnnotationAPI from '../../restAPI/AnnotationAPI';
import dayjs from 'dayjs';

const { Text } = Typography;

const MAX_FILES = 100;
const MAX_FILE_SIZE_MB = 125;

const ALLOWED_MIME = [
    'application/pdf',   
];

function fmtBytes(b) {
    if (!b) return '0 B';
    return b < 1024 * 1024
        ? `${(b / 1024).toFixed(1)} KB`
        : `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

const MultiDocumentUploader = forwardRef(function MultiDocumentUploader({ 
    disabled = false, 
    onFilesUploaded,
    onQueueChange,
    onHasPendingChange,
    onUploadingChange,
    onHasNameErrorsChange,
    token,
    companyId,
    meetingCommonInfo,
    currentCompanyInfo, 
    beforeUpload,
}, ref) {

    const [queue, setQueue] = useState([]);
    const [uploading, setUploading] = useState(false);
    const abortControllers = useRef({});
    const [api, ctx] = notification.useNotification();
    const fileCount = useRef(0);

    const { token: { colorBgContainer, colorPrimary, borderRadiusLG, colorTextDescription } } = theme.useToken();

    const stats = useMemo(() => {
        return {
        selected:   queue.length,
        uploaded:   queue.filter(f => f.status === 'done').length,
        inProgress: queue.filter(f => f.status === 'uploading').length,
        failed:     queue.filter(f => f.status === 'error').length,
        totalSize:  queue.reduce((s, f) => s + (f.size || 0), 0),
    }
    },[queue]) ;

    useEffect(() => {
        fileCount.current = 0;
    },[queue])

    
    const uploadSingleFile = async (fileItem) => {
        const { uid, fileObj, size } = fileItem;
       
        
        try {
            const attachmentId = AnnotationAPI.generateAttachmentId();
            const meetingId = meetingCommonInfo?.meetingId || null;
            const meetingDate = meetingCommonInfo?.meetingDate || null;
            const originalDate = meetingCommonInfo?.originalDate ? dayjs(meetingCommonInfo.originalDate) : null;
                        

            const annotationResponse = await AnnotationAPI.uploadPdfToAnnotation(
                fileObj,
                attachmentId,
                currentCompanyInfo?.userDetailsId,
                token,
                companyId,
                meetingId,
                meetingDate,
                originalDate,
                progress => setQueue(prev => prev.map(f => f.uid === uid ? {...f,progress} : {...f}))
            );

            if (!annotationResponse.success) {
                throw new Error(annotationResponse.error || 'Failed to upload to annotation system');
            }

            const { annotationUrl, session } = annotationResponse;

            
            setQueue(prev => {
                const updated = prev.map(x => x.uid === uid ? { 
                    ...x, 
                    progress: 100, 
                    status: 'done', 
                    loaded: size,
                    annotationUrl,
                    uploadedName: x.name
                } : x);
                
                
                const completedFiles = updated.filter(x => x.status === 'done').map(x => ({ 
                    name: x.uploadedName || x.name, 
                    size: x.size, 
                    fileObj: x.fileObj,
                    annotationUrl: x.annotationUrl
                }));
                onFilesUploaded?.(completedFiles);
                
                return updated;
            });

        } catch (error) {
            console.error('Upload error for', fileItem.name, error);
            setQueue(prev => prev.map(x => x.uid === uid ? { 
                ...x, 
                status: 'error', 
                progress: 0, 
                loaded: 0, 
                error: error.message || 'Upload failed' 
            } : x));
        }
    };

    const startUpload = async () => {
        const pending = queue.filter(f => f.status === 'pending' || f.status === 'error');
        if (!pending.length) return;
        if (queue.some(f => f.nameError)) return;

        try{
            await beforeUpload();
            setUploading(true);
            setQueue(prev => prev.map(f =>
                f.status === 'pending' || f.status === 'error'
                    ? { ...f, status: 'uploading', progress: 0, loaded: 0, error: null }
                    : f
            ));

            // upload all files in parallel
            await Promise.all(pending.map(fileItem => uploadSingleFile(fileItem)));
            
            setUploading(false);
        } catch(e){
            console.log('error validating document form: ',e);
        }
        
        
    };

    const retry = async (uid) => {
        if (abortControllers.current[uid]) { 
            abortControllers.current[uid].abort(); 
            delete abortControllers.current[uid]; 
        }
        const f = queue.find(x => x.uid === uid);
        if (!f) return;
        
        setQueue(prev => prev.map(x => x.uid === uid ? { ...x, status: 'uploading', progress: 0, loaded: 0, error: null } : x));
        setUploading(true);
        await uploadSingleFile(f);
        setUploading(false);
    };

    const remove = (uid) => {
        if (abortControllers.current[uid]) { 
            abortControllers.current[uid].abort(); 
            delete abortControllers.current[uid]; 
        }
        setQueue(prev => {
            const updated = prev.filter(f => f.uid !== uid);
            onQueueChange?.(updated.length);
            return updated;
        });
    };

    const applyNameEdit = (uid) => {
        setQueue(prev => prev.map(f => {
            if (f.uid !== uid) return f;
            const newName = (f.editValue ?? f.name).trim();
            if (!newName || newName.length > 150) {
                return { ...f, nameError: true, isEditingName: true };
            }
            return { ...f, name: newName, nameError: false, editValue: newName, isEditingName: false };
        }));
    };


    const uploadProps = {
        multiple: true,        
        showUploadList: false,
        accept: '.pdf',        
        disabled: disabled || uploading || queue.length >= MAX_FILES,
        beforeUpload: (file) => {            
            if (!ALLOWED_MIME.includes(file.type)) {
                api.error({ message: 'Invalid file type', description: `"${file.name}" is not an allowed type.`, placement: 'bottomLeft' });
                return Upload.LIST_IGNORE;
            }
            if (file.size / (1024 * 1024) > MAX_FILE_SIZE_MB) {
                api.error({ message: 'File too large', description: `"${file.name}" exceeds ${MAX_FILE_SIZE_MB} MB.`, placement: 'bottomLeft' });
                return Upload.LIST_IGNORE;
            }            
            if (queue.some(f => f.name === file.name && f.size === file.size)) {
                api.warning({ message: 'Duplicate', description: `"${file.name}" is already queued.`, placement: 'bottomLeft' });
                return Upload.LIST_IGNORE;
            }
            fileCount.current = fileCount.current + 1;
            if (queue.length >= MAX_FILES || fileCount.current >= MAX_FILES) {                                
                api.warning({ message: 'Limit reached', description: `Max ${MAX_FILES} files per upload.`, placement: 'bottomLeft' });
                return Upload.LIST_IGNORE;
            }
            const uid = `${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
            const hasNameError = file.name.length > 150;
            setQueue(prev => {
                const updated = [...prev, { uid, name: file.name, size: file.size, fileObj: file, status: 'pending', progress: 0, loaded: 0, error: null, nameError: hasNameError, editValue: file.name, isEditingName: hasNameError }];
                onQueueChange?.(updated.length);
                return updated;
            });
            return false;
        },
    };


    const rowColor = (f) => {
        if (f.nameError)              return '#ff4d4f';
        if (f.status === 'done')      return '#52c41a'; // antd colorSuccess
        if (f.status === 'error')     return '#ff4d4f'; // antd colorError
        if (f.status === 'uploading') return colorPrimary;
        return colorTextDescription;
    };

    const rowLabel = (f) => {
        if (f.nameError) return 'Invalid Name';
        return { done: 'Uploaded', error: 'Failed', uploading: 'Uploading...', pending: 'Queued' }[f.status] ?? 'Queued';
    };

    const rowDetail = f =>
        f.nameError              ? 'Name exceeds 150 characters — edit above to fix' :
        f.status === 'done'      ? 'Complete' :
        f.status === 'uploading' ? `${fmtBytes(f.loaded)} of ${fmtBytes(f.size)}` :
        f.status === 'error'     ? (f.error || 'Connection timeout') :
        'Waiting — click Upload to start';

    const hasPending = queue.some(f => f.status === 'pending' || f.status === 'error');
    const hasNameErrors = queue.some(f => f.nameError);

    useImperativeHandle(ref, () => ({ startUpload }));

    useEffect(() => { onHasPendingChange?.(hasPending); }, [hasPending]);
    useEffect(() => { onUploadingChange?.(uploading); }, [uploading]);
    useEffect(() => { onHasNameErrorsChange?.(hasNameErrors); }, [hasNameErrors]);

    return (
        <>
            {ctx}
        
            <Upload.Dragger {...uploadProps} style={{ marginBottom: 16 }}>
                <p style={{ fontWeight: 600, marginBottom: 4 }}>
                    Drag &amp; drop multiple files here
                </p>              
                <Flex justify="center" gap={8} wrap="wrap" style={{ marginBottom: 16 }}>
                    <Tag>Max {MAX_FILES} files per upload</Tag>                                        
                </Flex>
                <Button
                    type="primary"
                    disabled={disabled || uploading || queue.length >= MAX_FILES}
                >
                    Browse files
                </Button>
            </Upload.Dragger>
            
            
            {queue.length > 0 && (
                <Flex
                    style={{
                        marginBottom: 16,
                        borderRadius: borderRadiusLG,
                        border: '1px solid #d9d9d9',
                        background: colorBgContainer,
                        overflow: 'hidden',
                    }}
                >
                    {[
                        { v: stats.selected,            label: 'Files selected', c: 'inherit'   },
                        { v: stats.uploaded,            label: 'Uploaded',       c: '#52c41a'   },
                        { v: stats.inProgress,          label: 'In progress',    c: colorPrimary },
                        { v: stats.failed,              label: 'Failed',         c: '#ff4d4f'   },
                        { v: fmtBytes(stats.totalSize), label: 'Total size',     c: '#fa8c16'   },
                    ].map(({ v, label, c }, i, a) => (
                        <div
                            key={label}
                            style={{
                                flex: 1,
                                textAlign: 'center',
                                padding: '10px 4px',
                                borderRight: i < a.length - 1 ? '1px solid #d9d9d9' : 'none',
                            }}
                        >
                            <div style={{ fontSize: 20, fontWeight: 700, color: c, lineHeight: 1.3 }}>{v}</div>
                            <div style={{ fontSize: 12, color: colorTextDescription, marginTop: 2 }}>{label}</div>
                        </div>
                    ))}
                </Flex>
            )}

            
            {queue.length > 0 && (
                <>
                    <Text style={{ display: 'block', fontSize: 12, fontWeight: 700, letterSpacing: 1.2, color: colorTextDescription, marginBottom: 10 }}>
                        UPLOAD QUEUE
                    </Text>
                    <Flex vertical gap={8}>
                        {queue.map(f => {
                            const fc    = rowColor(f);
                            const isErr = f.status === 'error';
                            const isUpl = f.status === 'uploading';
                            const isDone = f.status === 'done';

                            return (
                                <div
                                    key={f.uid}
                                    style={{
                                        padding: '10px 14px 8px',
                                        borderRadius: borderRadiusLG,
                                        border: '1px solid #d9d9d9',
                                        borderLeft: `4px solid ${fc}`,
                                        background: colorBgContainer,
                                    }}
                                >
                                    {/* filename · size · status label · remove */}
                                    <Flex justify="space-between" align="center" style={{ marginBottom: 6 }}>
                                        {f.isEditingName ? (
                                            <Input
                                                size="small"
                                                value={f.editValue ?? f.name}
                                                onChange={(e) => setQueue(prev => prev.map(x => x.uid === f.uid ? { ...x, editValue: e.target.value } : x))}
                                                onBlur={() => applyNameEdit(f.uid)}
                                                onPressEnter={() => applyNameEdit(f.uid)}
                                                status={f.nameError ? 'error' : undefined}
                                                style={{ maxWidth: '55%' }}
                                                disabled={uploading}
                                                placeholder="Enter a name ≤ 150 characters"
                                                autoFocus
                                            />
                                        ) : (
                                            <Flex align="center" gap={4} style={{ maxWidth: '55%', minWidth: 0 }}>
                                                <Text
                                                    ellipsis
                                                    title={f.name}
                                                    style={{ fontWeight: 500, color: isUpl ? colorPrimary : 'inherit', minWidth: 0 }}
                                                >
                                                    {f.name}
                                                </Text>
                                                {!uploading && !isDone && !isUpl && (
                                                    <Button
                                                        type="primary"
                                                        size="small"
                                                        icon={<EditOutlined style={{ fontSize: 14 }} />}
                                                        onClick={() => setQueue(prev => prev.map(x => x.uid === f.uid ? { ...x, isEditingName: true, editValue: x.name } : x))}
                                                        style={{ 
                                                            backgroundColor: colorPrimary,
                                                            borderColor: colorPrimary,
                                                            padding: '4px 8px',                                                            
                                                            flexShrink: 0,
                                                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                                                        }}
                                                    />
                                                )}
                                            </Flex>
                                        )}
                                        <Flex align="center" gap={12}>
                                            <Text style={{ color: colorTextDescription }}>{fmtBytes(f.size)}</Text>
                                            <Text style={{ color: fc, fontWeight: 600, minWidth: 85, textAlign: 'right' }}>
                                                {rowLabel(f)}
                                            </Text>
                                            {!uploading  && f.status != 'done' && (
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    icon={<CloseOutlined style={{ fontSize: 11 }} />}
                                                    onClick={() => remove(f.uid)}
                                                    style={{ color: colorTextDescription, padding: 0, minWidth: 'unset', height: 'auto' }}
                                                />
                                            )}
                                        </Flex>
                                    </Flex>

                                    {/* progress bar */}
                                    <Progress
                                        percent={f.progress}
                                        status={f.nameError || isErr ? 'exception' : f.status === 'done' ? 'success' : 'active'}
                                        showInfo={false}
                                        size="small"
                                        strokeColor={f.nameError || isErr ? '#ff4d4f' : f.status === 'done' ? '#52c41a' : colorPrimary}
                                        style={{ margin: '2px 0' }}
                                    />

                                    {/* detail text · percentage */}
                                    <Flex justify="space-between" align="center" style={{ marginTop: 4 }}>
                                        <Flex align="center">
                                            <Text style={{ color: isErr ? '#ff4d4f' : colorTextDescription }}>
                                                {rowDetail(f)}
                                            </Text>
                                            {isErr && (
                                                <Button
                                                    type="link"
                                                    size="small"
                                                    icon={<ReloadOutlined />}
                                                    onClick={() => retry(f.uid)}
                                                    disabled={uploading}
                                                    style={{ color: '#ff4d4f', paddingLeft: 4, height: 'auto' }}
                                                >
                                                    click retry
                                                </Button>
                                            )}
                                        </Flex>
                                        <Text style={{ fontWeight: 600, color: fc }}>{f.progress}%</Text>
                                    </Flex>
                                </div>
                            );
                        })}
                    </Flex>
                </>
            )}
        </>
    );
});

export default MultiDocumentUploader;
